/**
 * The Perspective workbooks' fixed exercises, copied from production's
 * `perspective-workbook-7day.tsx` and `perspective-workbook-30day.tsx` at
 * `11ca09a`.
 *
 * **Extracted, not transcribed.** Regenerate rather than hand-edit.
 *
 * These are not the report sevenDayReset / thirtyDayPractice plans.
 * Production Perspective workbooks run a **fixed** programme -- Name the
 * Lens, Catch the Self-Talk, Locate the Time Orientation, and so on -- the same
 * seven days for every customer, with the personalised plan shown alongside it.
 * V2 rendered the Bottleneck workbook shape for both types and had none of it.
 */
export interface WorkbookExercise {
  n: number;
  title: string;
  subtitle: string;
  prompts: readonly string[];
}

/** Production's seven fixed days. */
export const PS_SEVEN_DAY_EXERCISES: readonly WorkbookExercise[] = [
  {
    n: 1,
    title: 'Name the Lens',
    subtitle: 'Notice what interpretation you reached for and what it made you assume.',
    prompts: [
      'What happened today that created pressure, uncertainty, discomfort, or self-doubt?',
      'What lens did you use to interpret it?',
      'What did that lens make you assume?',
    ],
  },
  {
    n: 2,
    title: 'Catch the Self-Talk',
    subtitle: 'Identify the exact internal sentence and where it comes from.',
    prompts: [
      'What exact sentence did you say to yourself?',
      'Was it supportive, punishing, doubtful, absolute, identity-based, or fear-based?',
      'What would you say to someone you respect if they were in the same situation?',
    ],
  },
  {
    n: 3,
    title: 'Locate the Time Orientation',
    subtitle: 'Notice whether your mind was in the past, the future, or the present.',
    prompts: [
      'Was your mind trying to control the future, replay the past, or act from the present?',
      'What were you trying to prevent, fix, avoid, or prove?',
      'What would present-based preparation or reflection look like?',
    ],
  },
  {
    n: 4,
    title: 'Find the Demand',
    subtitle: 'Locate the expectation underneath the pressure.',
    prompts: [
      'Did your self-talk include words like should, must, always, never, need, or can\'t?',
      'What expectation was underneath the pressure?',
      'Is that expectation reasonable, useful, and fully true?',
    ],
  },
  {
    n: 5,
    title: 'Debate the Belief',
    subtitle: 'Challenge the belief that is driving your reaction.',
    prompts: [
      'What belief was driving your reaction?',
      'What evidence supports it?',
      'What evidence challenges it?',
      'What is a more rational, useful belief?',
    ],
  },
  {
    n: 6,
    title: 'Reframe Into Action',
    subtitle: 'Identify the present-based action that is actually available to you.',
    prompts: [
      'What is one present-based action you can take?',
      'What is inside your control?',
      'What can you release because it is not yours to control?',
    ],
  },
  {
    n: 7,
    title: 'Install the New Statement',
    subtitle: 'Retire the old interpretation and install the new operating statement.',
    prompts: [
      'What old interpretation are you retiring?',
      'What new self-talk statement are you practicing?',
      'How will you use this next time pressure shows up?',
    ],
  },
];

/** The three questions production closes the seven days with. */
export const PS_SEVEN_DAY_REFLECTION: readonly string[] = [
  'What pattern showed up most consistently across the 7 days?',
  'What day created the most resistance, and what does that tell you?',
  'What is the one shift you are carrying forward?',
];

export interface WorkbookWeek {
  n: number;
  theme: string;
  goal: string;
  intro: string;
  dailyPrompts: readonly string[];
}

/** Production's fixed four weeks in the 30-day workbook. */
export const PS_THIRTY_DAY_WEEKS: readonly WorkbookWeek[] = [
  {
    n: 1,
    theme: 'Awareness',
    goal: 'Notice your dominant lens without judging it.',
    intro:
      'This week is about observation, not change. Your only job is to notice the perspective pattern when it shows up. Do not try to fix it yet. Just name it.',
    dailyPrompts: [
      'What situation triggered pressure, uncertainty, or discomfort today?',
      'What Perspective Pattern showed up?',
      'What did it help you notice?',
      'What did it cost you?',
      'What would you like to notice sooner tomorrow?',
    ],
  },
  {
    n: 2,
    theme: 'Self-Talk Replacement',
    goal: 'Replace one unhelpful internal sentence per day.',
    intro:
      'This week you will catch the exact sentence and replace it with one that is more rational and useful. A replacement does not need to feel true immediately. It needs to be more accurate and more actionable.',
    dailyPrompts: [
      'What was the unhelpful self-talk sentence?',
      'Which category did it fall into?',
      'What is the replacement sentence?',
      'How did the replacement sentence change your next action?',
    ],
  },
  {
    n: 3,
    theme: 'Belief Debate',
    goal: 'Use the ABCDE model on one real situation each day.',
    intro:
      'The ABCDE model helps you interrupt a pattern by walking through the full chain: what activated it, what you believed, what it cost you, how to dispute the belief, and what a more effective outcome looks like.',
    dailyPrompts: [
      'A (Activating Event) — What happened?',
      'B (Belief) — What did you believe it meant?',
      'C (Consequence) — How did that belief affect your body, emotion, or action?',
      'D (Debate) — Is the belief completely true, partially true, or just familiar?',
      'E (Effective New Belief) — What belief would help you operate better?',
    ],
  },
  {
    n: 4,
    theme: 'Present Action',
    goal: 'Take useful action before certainty, comfort, or approval is fully available.',
    intro:
      'This week is about execution. You do not need full confidence, full certainty, or full comfort before acting. You need a next useful step. That is all.',
    dailyPrompts: [
      'What action did you delay or avoid today?',
      'What were you waiting for?',
      'What was the next useful step?',
      'What happened when you acted from the present?',
      'What did you learn from the result?',
    ],
  },
];

/** Week 2's self-talk categories, which production lists on that week's page. */
export const PS_SELF_TALK_CATEGORIES: readonly string[] = [
  'Future control',
  'Past replay',
  'Approval seeking',
  'Comfort protection',
  'Outcome identity',
  'Negative forecasting',
  'Self-debate',
];

/** The two questions production closes every week with. */
export const PS_WEEKLY_REVIEW: readonly string[] = [
  'What worked this week?',
  'What did not work, and what will you change next week?',
];

/** The four questions production closes the thirty days with. */
export const PS_THIRTY_DAY_REFLECTION: readonly string[] = [
  'What Perspective Pattern showed up most often across the 30 days?',
  'What changed in your self-talk?',
  'What interpretation trap do you now catch faster?',
  'What present-based action will you continue practicing?',
];

/** The fifth is a writing exercise rather than a question. */
export const PS_THIRTY_DAY_STATEMENT_PROMPT =
  'What is your new operating statement? Write it below and keep it visible.';

/** Production's three closing steps on the 30-day workbook. */
export const PS_THIRTY_DAY_CLOSING = [
  {
    title: 'Return to the Perspective Assessment.',
    body:
      'Your reassessment will show how your pattern scores have shifted and whether your dominant pattern has changed. Compare the new result to your original report.',
  },
  {
    title: 'Keep your operating statement in place.',
    body:
      'The new response only becomes automatic through continued use. The practice does not end here. It moves from structured to habitual.',
  },
  {
    title: 'Watch for regression under pressure.',
    body:
      'High-stress periods activate old patterns. The traps marked active in your report are the ones most likely to resurface. Catching them early is what makes the difference.',
  },
];

/** Production's three closing steps on the 7-day Perspective workbook. */
export const PS_SEVEN_DAY_CLOSING = [
  {
    title: 'Keep your operating statement visible.',
    body: 'The new self-talk you wrote on Day 7 needs repetition to become automatic. Place it somewhere you see it daily.',
  },
  {
    title: 'Start the 30-Day New Mindset Practice Workbook.',
    body: 'Four weeks of structured practice build on the awareness you developed this week. Open it from your report page or dashboard.',
  },
  {
    title: 'Reassess in 30 days.',
    body: 'After completing both workbooks, return to the Perspective Assessment. Your pattern scores will show where movement has occurred.',
  },
];

/** The line production prints above those steps. */
export const PS_SEVEN_DAY_CLOSING_LEAD =
  'You have completed 7 days of pattern interruption. Now move to the 30-Day Practice.';

/** What production says the 30-day practice is, on its cover. */
export const PS_WHAT_THIS_PRACTICE_IS =
  'This 30-day practice is not about forcing positive thinking. It is about learning to notice the lens your mind is using, question unhelpful assumptions, and choose a more useful interpretation before taking action. Complete one week at a time. Do the weekly review at the end of each week before moving on.';
